<template>
  <div class="bg-white border-b border-gray-200 px-4 py-4 sm:px-6">
    <div class="max-w-[1400px] mx-auto flex flex-row flex-wrap items-center gap-4">
      <div class="flex-1 relative w-full sm:w-auto basis-full sm:basis-auto">
        <Search
          :size="20"
          class="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
        />
        <slot name="search-box"></slot>
      </div>

      <!-- Home Type Quick Filter -->
      <div class="flex items-center gap-2 overflow-x-auto no-scrollbar py-1">
        <button
          v-for="type in homeTypes"
          :key="type.value"
          type="button"
          :class="[
            'flex items-center gap-2 px-3 py-2 rounded-lg border transition-all whitespace-nowrap text-sm font-medium cursor-pointer',
            filters.homeType.includes(type.value)
              ? 'bg-brand text-white border-brand shadow-sm'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
          ]"
          @click="toggleHomeType(type.value)"
        >
          <component :is="type.icon" :size="16" />
          <span>{{ t('enums.homeType.' + type.value) }}</span>
          <span
            v-if="facets?.['index.homeType']?.[type.value]"
            :class="[
              'text-xs',
              filters.homeType.includes(type.value) ? 'text-white/80' : 'text-gray-400'
            ]"
          >
            {{ facets['index.homeType'][type.value] }}
          </span>
        </button>
      </div>

      <button
        :class="['w-auto flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer transition', { 'bg-blue-100 border-blue-200': isFiltersActive }]"
        @click="$emit('toggle-filters')"
      >
        <SlidersHorizontal :size="16" />
        {{ t('searchResults.filters') }}
        <span v-if="isFiltersActive" class="h-2 w-2 bg-brand rounded-full"></span>
      </button>

      <div class="flex items-center gap-2 border border-gray-300 rounded-lg p-1 ml-auto sm:ml-0">
        <button
          v-if="!!place"
          :class="[
            'p-2 rounded transition-colors cursor-pointer',
            viewMode === 'grid' ? 'bg-gray-200' : 'hover:bg-gray-100',
          ]"
          :aria-label="t('searchResults.gridView')"
          @click="$emit('update:viewMode', 'grid')"
        >
          <Grid :size="16" />
        </button>
        <button
          :class="[
            'p-2 rounded transition-colors cursor-pointer',
            viewMode === 'list' ? 'bg-gray-200' : 'hover:bg-gray-100',
          ]"
          :aria-label="t('searchResults.listView')"
          @click="$emit('update:viewMode', 'list')"
        >
          <List :size="16" />
        </button>
        <button
          :class="[
            'p-2 rounded transition-colors cursor-pointer',
            viewMode === 'map' ? 'bg-gray-200' : 'hover:bg-gray-100',
          ]"
          :aria-label="t('searchResults.mapView')"
          @click="$emit('update:viewMode', 'map')"
        >
          <MapIcon :size="16" />
        </button>
      </div>
    </div>
    <slot name="filters-panel"></slot>
  </div>
</template>

<script setup lang="ts">
import { computed, type Component } from 'vue';
import { Search, SlidersHorizontal, Grid, List, Map as MapIcon, Home, Building2, Mountain, Warehouse } from 'lucide-vue-next';
import { useI18n } from 'vue-i18n';
import type { LinkedPage, Page, Filters, HomeType, Facets } from '@repo/types'

const props = defineProps<{
  filters: Filters;
  viewMode: string;
  place: Page | LinkedPage | null;
  facets?: Facets;
}>();

const emit = defineEmits(['toggle-filters', 'update:viewMode', 'update:filters']);

const { t } = useI18n();

const homeTypes: Array<{ icon: Component, value: HomeType }> = [
  { icon: Building2, value: 'condo' },
  { icon: Home, value: 'house' },
  { icon: Mountain, value: 'land' },
  { icon: Warehouse, value: 'other' },
]

function toggleHomeType(value: HomeType) {
  const current = props.filters.homeType || [];
  const updated = current.includes(value)
    ? current.filter(v => v !== value)
    : [...current, value];

  emit('update:filters', { ...props.filters, homeType: updated });
}

const isFiltersActive = computed(() => {
  if (!props.filters) return false;
  return [
    props.filters.labels?.length > 0,
    props.filters.price?.min > 0 || props.filters.price?.max !== undefined,
    props.filters.size?.min > 0 || props.filters.size?.max !== undefined
  ].filter(Boolean).length > 0;
});
</script>
